/**
 * MarIA Health Care - Encuesta de Salud Integral
 *
 * Pagina de encuesta usando SurveyJS para una experiencia UX optima.
 *
 * Features:
 * - Progress bar con navegacion por secciones
 * - Guardado automatico parcial
 * - Responsive mobile-first
 * - Accesible (WCAG 2.1 AA)
 * - Tema personalizado MarIA
 * - Protección contra re-edición de encuesta completada
 *
 * @see https://surveyjs.io/form-library/documentation/get-started-react
 */
import { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';

// SurveyJS CSS - v2.0.0+ usa survey-core.css en lugar de defaultV2.min.css
import 'survey-core/survey-core.min.css';

// Configuracion
import { surveyJson } from '@/config/surveySchema';
import { useAuth } from '@/contexts/AuthContext';
import { Navbar } from '@/components/layout/Navbar';

// API base URL
// Misma variable que el resto de la app (axios.ts, ChatPage). Sin fallback a
// producción: en desarrollo apunta al origen actual, nunca a datos reales.
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Custom theme para MarIA basado en los colores de la app
 */
const mariaTheme = {
  cssVariables: {
    // Primary colors (matching Tailwind primary - brand-600)
    '--sjs-primary-backcolor': 'rgb(219, 39, 119)', // brand-600 (MarIA pink)
    '--sjs-primary-backcolor-light': 'rgba(219, 39, 119, 0.1)',
    '--sjs-primary-backcolor-dark': 'rgb(190, 24, 93)', // brand-700
    '--sjs-primary-forecolor': 'rgb(255, 255, 255)',
    '--sjs-primary-forecolor-light': 'rgba(255, 255, 255, 0.25)',

    // Secondary colors
    '--sjs-secondary-backcolor': 'rgb(243, 244, 246)', // gray-100
    '--sjs-secondary-forecolor': 'rgb(31, 41, 55)', // gray-800

    // General
    '--sjs-general-backcolor': 'rgb(255, 255, 255)',
    '--sjs-general-backcolor-dim': 'rgb(249, 250, 251)', // gray-50
    '--sjs-general-forecolor': 'rgb(17, 24, 39)', // gray-900
    '--sjs-general-forecolor-light': 'rgb(107, 114, 128)', // gray-500

    // Border
    '--sjs-border-default': 'rgb(229, 231, 235)', // gray-200
    '--sjs-border-light': 'rgb(243, 244, 246)', // gray-100

    // Shadow
    '--sjs-shadow-small': '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '--sjs-shadow-medium': '0 4px 6px -1px rgba(0, 0, 0, 0.1)',

    // Font
    '--sjs-font-family': 'ui-sans-serif, system-ui, sans-serif',
    '--sjs-font-size': '16px',

    // Corner radius
    '--sjs-corner-radius': '8px',

    // Base sizing
    '--sjs-base-unit': '8px',
  },
  themeName: 'maria',
  colorPalette: 'light',
  isPanelless: false,
};

export default function EncuestaSaludPage() {
  const navigate = useNavigate();
  const { user, getIdToken, checkSurveyStatus } = useAuth();
  const [survey, setSurvey] = useState<Model | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  // true cuando la encuesta ya estaba completada y la usuaria la está editando
  // (modo actualización de perfil). Solo cambia textos, NO bloquea el formulario.
  const [isEditing, setIsEditing] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initRef = useRef(false);

  /**
   * Check if survey is already completed
   */
  const checkIfCompleted = useCallback(async () => {
    const token = await getIdToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_BASE}/api/survey/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.completada === true;
      }
    } catch (error) {
      console.error('Error checking survey status:', error);
    }
    return false;
  }, [getIdToken]);

  /**
   * Cargar datos previos de la encuesta
   */
  const loadSurveyData = useCallback(async () => {
    const token = await getIdToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_BASE}/api/survey`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.respuestas || null;
      }
    } catch (error) {
      console.error('Error loading survey data:', error);
    }
    return null;
  }, [getIdToken]);

  /**
   * Guardar datos de la encuesta (parcial o completo)
   */
  const saveSurveyData = useCallback(async (data: Record<string, unknown>, isComplete: boolean) => {
    const token = await getIdToken();
    if (!token) return;

    setIsSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/survey`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          respuestas: data,
          completada: isComplete,
        }),
      });

      if (response.ok) {
        setLastSaved(new Date());
      }
    } catch (error) {
      console.error('Error saving survey data:', error);
    } finally {
      setIsSaving(false);
    }
  }, [getIdToken]);

  /**
   * Inicializar el modelo de SurveyJS
   */
  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (initRef.current) return;
    initRef.current = true;

    const initSurvey = async () => {
      setIsLoading(true);

      // Si la encuesta ya estaba completada, NO bloqueamos: entramos en modo
      // edición. La usuaria puede actualizar su perfil de salud cuando su
      // situación cambie (embarazo, nueva alergia, dejar de fumar, etc.).
      // Los datos previos se precargan más abajo con loadSurveyData() y el
      // backend hace merge + registra cada cambio en el audit log inmutable.
      const completed = await checkIfCompleted();
      setIsEditing(completed);

      // Crear modelo
      const surveyModel = new Model(surveyJson);

      // Aplicar tema MarIA
      surveyModel.applyTheme(mariaTheme);

      // Configurar textos en espanol
      surveyModel.locale = 'es';

      // Cargar datos previos si existen
      const previousData = await loadSurveyData();
      if (previousData) {
        surveyModel.data = previousData;
      }

      // Evento: Guardado parcial al cambiar de pagina
      surveyModel.onCurrentPageChanged.add((sender) => {
        saveSurveyData(sender.data, false);
      });

      // Evento: Guardado parcial al cambiar valor (debounced)
      surveyModel.onValueChanged.add((sender) => {
        // Clear previous timeout
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        // Debounce: guardar cada 3 segundos maximo
        saveTimeoutRef.current = setTimeout(() => {
          saveSurveyData(sender.data, false);
        }, 3000);
      });

      // Evento: Encuesta completada
      // FIX: Await save + checkSurveyStatus before navigation to prevent
      // PrivateRoute from redirecting back to /mi-salud due to stale state
      surveyModel.onComplete.add(async (sender) => {
        // Clear any pending save
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        // Wait for save to complete
        await saveSurveyData(sender.data, true);

        // Update AuthContext state so PrivateRoute allows access to /chat
        await checkSurveyStatus();

        // Navigate immediately - state is now updated
        navigate('/chat');
      });

      setSurvey(surveyModel);
      setIsLoading(false);
    };

    initSurvey();

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [checkIfCompleted, loadSurveyData, saveSurveyData, navigate, checkSurveyStatus]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando encuesta...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Status bar */}
      <div className="bg-secondary/50 border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-2 flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">
              {isEditing ? 'Actualizar tu Perfil de Salud' : 'Tu Perfil de Salud'}
            </span>
            {user?.displayName && (
              <span className="text-foreground font-medium">- {user.displayName}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isSaving ? (
              <span className="text-muted-foreground flex items-center gap-1">
                <span className="animate-pulse">*</span> Guardando...
              </span>
            ) : lastSaved ? (
              <span className="text-green-600 flex items-center gap-1">
                Guardado
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Survey container */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {survey && <Survey model={survey} />}
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 py-4 text-center text-xs text-muted-foreground">
          <p>
            Tus datos estan cifrados y se utilizan exclusivamente para personalizar tu experiencia
            y para fines de investigacion universitaria de forma anonima.
          </p>
          <p className="mt-1">
            Contacto investigacion:{' '}
            <a href="mailto:mariaeva.perez@um.es" className="text-primary hover:underline">
              mariaeva.perez@um.es
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
